/**
 * EO «Бэкап медиа» (§0.1.2, perm media_backup). Манифест всех медиа на локальный
 * диск с каноническими именами — страховка от повреждения Telegram-канала.
 *
 *  - Счётчик + список ассетов (имя файла, kind, владелец, sha256, размер).
 *  - «Экспорт манифеста» → скачивание unibooking-media-manifest.json клиентом
 *     через Blob (без серверного зипа — файлов много, §0.1.2).
 *  - Поимённые ссылки на скачивание: <a download> на GET /api/eo/media/:id/download
 *     (бэк отдаёт Content-Disposition: attachment; filename="<каноническое имя>").
 *
 * EO accent = фуксия (--role под data-role=eo). SVG-иконки, без эмодзи.
 */
import { useCallback, useMemo, useState } from "react";
import { useMediaBackup, type BackupAsset } from "../../hooks/useMediaBackup";
import { Download, Clock, Hash, Photo, VideoIco, Lock, WarnTri, NavBase } from "../oo/v8icons";
import css from "./mediabackup.module.css";

/** API origin (так же, как api-хелпер): прод → VITE_API_URL, dev → "". */
const API_BASE: string = ((import.meta.env?.VITE_API_URL as string | undefined) ?? "").replace(/\/$/, "");

function fmtBytes(n: number): string {
  if (!n) return "0 Б";
  const u = ["Б", "КБ", "МБ", "ГБ"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}
function totalBytes(assets: BackupAsset[]): number {
  return assets.reduce((s, a) => s + (a.bytes || 0), 0);
}
function genAtLabel(iso: string): string {
  const m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}` : iso;
}

export function MediaBackup() {
  const { manifest, assets, count, loading, error, reload } = useMediaBackup();
  const [q, setQ] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return assets;
    return assets.filter(
      (a) =>
        a.fileName.toLowerCase().includes(t) ||
        (a.origName ?? "").toLowerCase().includes(t) ||
        a.ownerStaffId.toLowerCase().includes(t) ||
        (a.label ?? "").toLowerCase().includes(t),
    );
  }, [assets, q]);

  const exportManifest = useCallback(() => {
    if (!manifest) return;
    try {
      const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = manifest.manifest || "unibooking-media-manifest.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flash(`Манифест выгружен (${count} файлов)`);
    } catch {
      flash("Не удалось выгрузить манифест");
    }
  }, [manifest, count, flash]);

  if (loading) {
    return (
      <div className={css.root}>
        <div className={css.summary}>
          <div className={css.sumLeft}><div className={css.sumCount}><span className={css.n}>…</span> файлов</div></div>
        </div>
        <div className={css.content}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={css.skel}>
              <div className={`${css.skBox} ${css.skelLine}`} />
              <div className={css.skLines}>
                <div className={css.skelLine} style={{ width: "75%" }} />
                <div className={css.skelLine} style={{ width: "45%" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.root}>
        <div className={css.emptyState}>
          <span className={css.esIco}><WarnTri /></span>
          <span className={css.esT}>Не удалось загрузить манифест</span>
          <span className={css.esS}>{error}</span>
          <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
        </div>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <div className={css.summary}>
        <div className={css.sumLeft}>
          <div className={css.sumCount}><span className={css.n}>{count}</span> {filesWord(count)}</div>
          <div className={css.sumMeta}>
            <Clock /> {manifest ? genAtLabel(manifest.generatedAt) : "—"}
            <span className={css.sumBytes}>· {fmtBytes(totalBytes(assets))}</span>
          </div>
        </div>
        <button type="button" className={css.export} disabled={count === 0} onClick={exportManifest}>
          <Download /> Экспорт манифеста
        </button>
      </div>

      <div className={css.searchRow}>
        <input
          className={css.search}
          placeholder="Поиск по имени / владельцу…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {assets.length === 0 ? (
        <div className={css.emptyState}>
          <span className={css.esIco}><NavBase width={40} height={40} /></span>
          <span className={css.esT}>Медиа нет</span>
          <span className={css.esS}>Когда операторы загрузят фото/видео, они появятся здесь.</span>
        </div>
      ) : (
        <div className={css.content}>
          <div className={css.hint}>
            <WarnTri />
            <span>
              Экспорт скачивает манифест JSON. Файлы тяните по одному — кнопкой справа от каждой строки.
              Имя файла начинается с mediaId — это ключ пересвязки при восстановлении канала.
            </span>
          </div>
          {filtered.length === 0 ? (
            <div className={css.emptyState} style={{ padding: "24px" }}>
              <span className={css.esT}>Ничего не найдено</span>
              <button type="button" className={css.esBtn} onClick={() => setQ("")}>Сбросить</button>
            </div>
          ) : (
            filtered.map((a) => <AssetRow key={a.mediaId} asset={a} />)
          )}
        </div>
      )}

      {toast ? <div className={css.toast}>{toast}</div> : null}
    </div>
  );
}

function AssetRow({ asset }: { asset: BackupAsset }) {
  const isVideo = asset.kind === "video";
  const href = `${API_BASE}${asset.downloadUrl}`;
  return (
    <div className={css.row}>
      <span className={`${css.thumb} ${isVideo ? css.video : ""}`}>
        {isVideo ? <VideoIco /> : <Photo />}
      </span>
      <div className={css.info}>
        <div className={css.fileName}>{asset.fileName}</div>
        <div className={css.meta}>
          <span className={css.kindTag}>{isVideo ? "видео" : "фото"}</span>
          {asset.fanOnly ? <span className={css.fanTag}><Lock /> fan</span> : null}
          <span className={css.owner}>{asset.ownerStaffId}</span>
          <span className={css.bytes}>{fmtBytes(asset.bytes)}</span>
          {asset.sha256 ? <span className={css.sha}><Hash /> {asset.sha256.slice(0, 8)}</span> : null}
        </div>
      </div>
      <a className={css.dl} href={href} download aria-label={`Скачать ${asset.fileName}`} title="Скачать файл">
        <Download />
      </a>
    </div>
  );
}

function filesWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "файл";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "файла";
  return "файлов";
}
